import { useState, useEffect, useLayoutEffect, useMemo } from 'react'

import { ColumnDef } from '@tanstack/react-table'
import { AdaptiveFilterButtons } from '@/features/adaptiveFilterButtons/AdaptiveFilterButtons'
import { SearchInput } from '@/shared/components/SearchInput/SearchInput'
import { FilterButtonModel } from '@/shared/components/MobileFilterButtons/MobileFilterButtons'
import { useDebounce } from '@/shared/hooks'
import { useIsMobile } from '@/shared/utils'
import { useReferralTopups } from '../../../hooks/useReferralTopups'
import { TopupsReferralListParamsStatusEnum } from 'syncra-api-client'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { DateRange } from 'react-day-picker'
import { getReferralTopupsColumns, ReferralTopupModel } from './columns'
import type { TraderTopupProfile } from 'syncra-api-client'
import { MobileBlockReferralTopup } from './MobileBlockReferralTopup'
import { removeSessionStorageItem } from '@/shared/lib/storage'
import { BaseTable, BaseTableSkeleton } from 'syncra-storybook/features'

type ReferralTopupModelWithId = ReferralTopupModel & { id: string }

interface ReferralTopupsPanelProps {
  referralId: string
  dateRange?: DateRange
}

export const ReferralTopupsPanel = ({ referralId, dateRange }: ReferralTopupsPanelProps) => {
  const [selectedStatusIndex, setSelectedStatusIndex] = useState<TopupsReferralListParamsStatusEnum>(TopupsReferralListParamsStatusEnum.Value1)
  const [skip, setSkip] = useState(0)
  const [query, setQuery] = useState('')
  const debouncedSelectedStatusIndex = useDebounce(selectedStatusIndex, 500)
  const debouncedQuery = useDebounce(query, 500)
  const isMobile = useIsMobile()

  // Статусы для фильтров (соответствуют API /v1/topups/referral) - мемоизируем чтобы избежать пересоздания
  const statusFilters = useMemo<FilterButtonModel[]>(() => [
    { index: TopupsReferralListParamsStatusEnum.Value1, label: 'all', name: 'Все' },
    { index: TopupsReferralListParamsStatusEnum.Value2, label: 'inProgress', name: 'Платёж выполняется' },
    { index: TopupsReferralListParamsStatusEnum.Value3, label: 'done', name: 'Завершенные' },
    { index: TopupsReferralListParamsStatusEnum.Value4, label: 'canceled', name: 'Отмененные' },
  ], [])

  // Очищаем сохраненную пагинацию при монтировании компонента синхронно
  // чтобы пагинация всегда начиналась с первой страницы при открытии вкладки
  // используем useLayoutEffect чтобы очистка произошла до рендера BaseTable
  useLayoutEffect(() => {
    const storageKey = `table_pagination_${window.location.pathname}`
    removeSessionStorageItem(storageKey)
  }, [])

  // Сброс пагинации при изменении поиска или статуса (до debounce, чтобы избежать лишних запросов)
  useEffect(() => {
    setSkip(0)
    // Очищаем сохраненную пагинацию из sessionStorage при смене фильтров
    const storageKey = `table_pagination_${window.location.pathname}`
    removeSessionStorageItem(storageKey)
  }, [query, selectedStatusIndex])

  const { data, loading, error } = useReferralTopups({
    referralId,
    status: debouncedSelectedStatusIndex,
    skip,
    take: DEFAULT_TAKE,
    dateRange,
    search: debouncedQuery,
  })

  const handlePageChange = (newSkip: number) => {
    setSkip(newSkip)
  }

  const itemsWithId: ReferralTopupModelWithId[] = data.items 
    ? data.items.filter((item: TraderTopupProfile): item is TraderTopupProfile & { id: string } => !!item.id).map(item => item as ReferralTopupModelWithId)
    : []

  return (
    <div className="flex flex-col gap-6">
      <div className="flex md:flex-row flex-col md:justify-between gap-4">
        {isMobile ? (
          <div className="flex gap-[10px] w-full">
            <AdaptiveFilterButtons
              filterButtons={statusFilters}
              selectedBtn={selectedStatusIndex}
              setSelectedBtn={(index) => {
                setSelectedStatusIndex(index as TopupsReferralListParamsStatusEnum)
              }}
              minWidthMobile="150px"
              className="flex-1"
            />
            <SearchInput
              placeholder="Поиск по номеру карты"
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1"
            />
          </div>
        ) : (
          <>
            <AdaptiveFilterButtons
              filterButtons={statusFilters}
              selectedBtn={selectedStatusIndex}
              setSelectedBtn={(index) => {
                setSelectedStatusIndex(index as TopupsReferralListParamsStatusEnum)
              }}
              minWidthMobile="150px"
              className="flex-1"
            />
            <SearchInput
              placeholder="Поиск по номеру карты"
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1"
            />
          </>
        )}
      </div>

      <div>
        {loading ? (
          <BaseTableSkeleton />
        ) : error ? (
          <div className="p-4 text-center">
            <p className="text-red-500">Ошибка загрузки данных: {error?.message || 'Неизвестная ошибка'}</p>
          </div>
        ) : !data.items || data.items.length === 0 ? (
          <p>Входящих платежей нет.</p>
        ) : (
          <BaseTable<ReferralTopupModelWithId, unknown>
            columns={getReferralTopupsColumns() as ColumnDef<ReferralTopupModelWithId, unknown>[]}
            data={itemsWithId}
            cardClasses="border-0"
            onPageChange={handlePageChange}
            skip={skip}
            totalItems={data.count}
            mobileCards={itemsWithId.map((el) => (
              <MobileBlockReferralTopup key={el.id} topup={el} />
            ))}
          />
        )}
      </div>
    </div>
  )
}

